import { api, type ApiResponse } from '../client';

export interface PaymentReportPayout {
  uuid: string;
  payout_id: string;
  amount: string;
  total_amount: string;
  transaction_id: string;
  transaction_date: string | null;
  date: string | null;
  payout_bsb_no: string;
  payout_acc_no: string;
  payout_acc_name: string;
  charity: {
    uuid: string;
    category_uuid: string;
    category: {
      uuid: string;
      name: string;
      description: string;
      is_active: boolean;
      sort_order: number;
    };
    name: string;
    abn: string;
    contact_email: string;
    contact_phone: string;
    website?: string | null;
    head_office_location: string;
    charity_government_id: string;
    verified: number;
    verified_bsb_no: string;
    verified_acc_no: string;
    verified_acc_name: string;
  };
  employer: {
    uuid: string;
    name: string;
    abn: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    head_office_location: string;
  };
  company: string;
  employer_count: number;
  donation_payout_status: {
    uuid: string;
    name: string;
  };
  status: string;
  total_donations: number;
}

export interface EmployeePaymentReport {
  donation_request_uuid: string;
  user_uuid: string;
  employee_name: string;
  charity_uuid: string;
  charity_name: string;
  leave_hours: number;
  leave_value: number;
  employer_top_up: number;
  created_at: string;
  approved_at: string;
  status: string;
  employer_comment?: string;
}

export interface EmployerStats {
  total_donations: number;
  total_hours: number;
  total_value: number;
  active_employees: number;
  pending_requests: number;
  approved_requests: number;
  rejected_requests: number;
}

export interface PaymentReportFilters {
  phrase?: string;
  status?: string;
  charity_uuid?: string;
  employer_uuid?: string;
  company?: string;
  start_date?: string;
  end_date?: string;
  date_from?: string;
  date_to?: string;
  sort_by?: 'amount' | 'charity' | 'employer' | 'company' | 'date' | 'status' | 'transaction_date' | 'created_at' | 'charity_name' | 'payout_date';
  sort_direction?: 'asc' | 'desc';
  column?: 'amount' | 'transaction_date' | 'created_at' | 'updated_at';
  order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
  all?: boolean;
}

export interface EmployeePaymentFilters {
  user_uuid?: string;
  phrase?: string;
  status?: string;
  charity_uuid?: string;
  start_date?: string;
  end_date?: string;
  sort_by?: 'employee_name' | 'charity_name' | 'leave_hours' | 'leave_value' | 'created_at' | 'status';
  sort_direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export interface PaymentReportResponse {
  success: boolean;
  message?: string;
  items?: PaymentReportPayout[]; // Paginated response uses 'items'
  data?: PaymentReportPayout[]; // Fallback for non-paginated responses
  paging?: {
    current_page: number;
    last_page: number;
    per_page: number;
    current_items: number;
    from: number;
    to: number;
    total: number;
  };
  errors: string[];
}

export interface EmployeePaymentResponse {
  success: boolean;
  message?: string;
  data: {
    current_page: number;
    data: EmployeePaymentReport[];
    per_page: number;
    total: number;
    last_page: number;
    from: number;
    to: number;
  };
}

export interface EmployerStatsResponse {
  success: boolean;
  message?: string;
  data: EmployerStats;
}

// API base URL
const API_BASE_URL = '/employer-payouts';
const EMPLOYEE_PAYMENT_URL = '/employee-payment-report';
const EMPLOYER_STATS_URL = '/employer-stats';
const COMPLETED_PAYMENT_STATS_URL = '/completed-payment-stats';

export class PaymentReportService {
  private static instance: PaymentReportService;

  private constructor() {}

  public static getInstance(): PaymentReportService {
    if (!PaymentReportService.instance) {
      PaymentReportService.instance = new PaymentReportService();
    }
    return PaymentReportService.instance;
  }

  /**
   * Get employer payouts grouped by charity for employer dashboard
   * @param filters Optional filters for the request
   * @returns Promise with the payment report data
   */
  public async getEmployerPayouts(filters?: PaymentReportFilters): Promise<PaymentReportResponse> {
    try {
      const response = await api.get<PaymentReportResponse>(API_BASE_URL, {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employer payouts:', error);
      throw error;
    }
  }

  /**
   * Get employee payment reports
   * @param filters Optional filters including user_uuid for specific employee
   * @returns Promise with the employee payment report data
   */
  public async getEmployeePaymentReport(filters?: EmployeePaymentFilters): Promise<EmployeePaymentResponse> {
    try {
      const url = filters?.user_uuid ? `${EMPLOYEE_PAYMENT_URL}/${filters.user_uuid}` : EMPLOYEE_PAYMENT_URL;
      const response = await api.get<EmployeePaymentResponse>(url, {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employee payment report:', error);
      throw error;
    }
  }

  /**
   * Get employer statistics
   * @param filters Optional filters for the stats
   * @returns Promise with the employer statistics
   */
  public async getEmployerStats(filters?: PaymentReportFilters): Promise<EmployerStatsResponse> {
    try {
      const response = await api.get<EmployerStatsResponse>(EMPLOYER_STATS_URL, {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employer stats:', error);
      throw error;
    }
  }

  /**
   * Get completed payment statistics (aligned with paid-invoices table)
   */
  public async getCompletedPaymentStats(filters?: PaymentReportFilters): Promise<EmployerStatsResponse> {
    try {
      const response = await api.get<EmployerStatsResponse>(COMPLETED_PAYMENT_STATS_URL, {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching completed payment stats:', error);
      throw error;
    }
  }

  /**
   * Get payout details by UUID
   * @param uuid The UUID of the payout
   * @returns Promise with the payout details
   */
  public async getPayoutDetails(uuid: string): Promise<PaymentReportPayout> {
    try {
      const response = await api.get<ApiResponse<PaymentReportPayout>>(`${API_BASE_URL}/${uuid}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching payout details:', error);
      throw error;
    }
  }

  /**
   * Export payment report data as CSV
   * @param filters Optional filters for the export
   * @returns Promise with the CSV blob
   */
  public async exportPaymentReport(filters?: PaymentReportFilters): Promise<Blob> {
    try {
      const response = await api.get<Blob>(`${API_BASE_URL}/export`, {
        params: { ...filters, format: 'csv' },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting payment report:', error);
      throw error;
    }
  }

  /**
   * Get payment report summary for dashboard
   * @param filters Optional filters for the summary
   * @returns Promise with summary data
   */
  public async getPaymentSummary(filters?: PaymentReportFilters): Promise<{
    total_amount: number;
    total_payouts: number;
    pending_payouts: number;
    completed_payouts: number;
  }> {
    try {
      const response = await api.get<ApiResponse<{
        total_amount: number;
        total_payouts: number;
        pending_payouts: number;
        completed_payouts: number;
      }>>(`${API_BASE_URL}/summary`, {
        params: filters
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching payment summary:', error);
      throw error;
    }
  }

  /**
   * Get outstanding payments (approved but not yet paid)
   * @returns Promise with outstanding payment data grouped by charity
   */
  public async getOutstandingPayments(): Promise<ApiResponse<{
    totalAmount: number;
    totalHours: number;
    totalDonations: number;
    charities: Array<{
      charity: {
        uuid: string;
        name: string;
      };
      donators: Array<{
        uuid: string;
        name: string;
        hours: number;
        amount: number;
        leave_value: number;
        employer_top_up: number;
        approved_at: string | null;
      }>;
      totalAmount: number;
      totalHours: number;
    }>;
  }>> {
    try {
      const response = await api.get<ApiResponse<{
        totalAmount: number;
        totalHours: number;
        totalDonations: number;
        charities: Array<{
          charity: {
            uuid: string;
            name: string;
          };
          donators: Array<{
            uuid: string;
            name: string;
            hours: number;
            amount: number;
            leave_value: number;
            employer_top_up: number;
            approved_at: string | null;
          }>;
          totalAmount: number;
          totalHours: number;
        }>;
      }>>('/outstanding-payments');
      return response.data;
    } catch (error) {
      console.error('Error fetching outstanding payments:', error);
      throw error;
    }
  }

  /**
   * Get paid invoices for payment history
   * @param filters Optional filters for the request
   * @returns Promise with paid invoices data
   */
  public async getPaidInvoices(filters?: PaymentReportFilters): Promise<PaymentReportResponse> {
    try {
      const response = await api.get<PaymentReportResponse>('/paid-invoices', {
        params: filters
      });
      const raw = response.data as { success?: boolean; data?: { items?: unknown[]; paging?: unknown } | unknown[]; paging?: unknown; items?: unknown[] };
      const d = raw.data;
      const items = (d && typeof d === 'object' && 'items' in d ? d.items : Array.isArray(d) ? d : []) ?? [];
      const paging = (d && typeof d === 'object' && 'paging' in d ? d.paging : raw.paging) ?? undefined;
      return { ...raw, data: items, items, paging } as PaymentReportResponse;
    } catch (error) {
      console.error('Error fetching paid invoices:', error);
      throw error;
    }
  }

  /**
   * Get paid invoice details by invoice ID
   * @param invoiceId Invoice ID (format: INV-YYYYMMDD) or date string in Y-m-d format
   * @returns Promise with paid invoice details (success, data, message)
   */
  public async getPaidInvoiceDetails(invoiceId: string): Promise<{
    success: boolean;
    data?: {
      invoice_id: string;
      invoice_date: string;
      paid_at: string;
      totalAmount: number;
      totalHours: number;
      totalDonations: number;
      totalContribution: number;
      totalCharities: number;
      charities: Array<{
        charity: { uuid: string; name: string };
        donators: Array<{
          uuid: string;
          name: string;
          hours: number;
          amount: number;
          leave_value: number;
          employer_top_up: number;
          approved_at: string | null;
        }>;
        totalAmount: number;
        totalHours: number;
        totalContribution?: number;
        payment_received?: number;
        l2g_fee_percentage?: number;
        l2g_fee_amount?: number;
        net_payment?: number;
        payment_reference?: string;
        transaction_id?: string;
        transaction_date?: string;
        status?: string;
      }>;
    };
    message?: string;
  }> {
    try {
      const response = await api.get<{
        success: boolean;
        data?: {
          invoice_id: string;
          invoice_date: string;
          paid_at: string;
          totalAmount: number;
          totalHours: number;
          totalDonations: number;
          totalContribution: number;
          totalCharities: number;
          charities: Array<{
            charity: { uuid: string; name: string };
            donators: Array<{
              uuid: string;
              name: string;
              hours: number;
              amount: number;
              leave_value: number;
              employer_top_up: number;
              approved_at: string | null;
            }>;
            totalAmount: number;
            totalHours: number;
            totalContribution?: number;
            payment_received?: number;
            l2g_fee_percentage?: number;
            l2g_fee_amount?: number;
            net_payment?: number;
            payment_reference?: string;
            transaction_id?: string;
            transaction_date?: string;
            status?: string;
          }>;
        };
        message?: string;
      }>(`/paid-invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching paid invoice details:', error);
      throw error;
    }
  }

  /**
   * Download paid invoice as PDF
   * @param invoiceId Invoice ID (e.g. INV-20260318)
   * @returns Promise that resolves when download has been triggered
   */
  public async downloadPaidInvoice(invoiceId: string): Promise<void> {
    const response = await api.get<Blob>(`/paid-invoices/${encodeURIComponent(invoiceId)}/download`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data as unknown as Blob], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${invoiceId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export default PaymentReportService.getInstance(); 